import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

// Components
import GatsbyHead from '../../components/layout/gatsby-head';
import Layout, { Container } from '../../components/layout';
import PageTitle from '../../components/page-title';
import ShopInfo from '../../components/shop-info';
import { FormAssembly } from '../../components/form';
import RichText from '../../components/rich-text';
import Assemblies from '../../components/assemblies';
import CollectionCTA from '../../components/shop-furniture/collection-cta';
import HeroNoCard from '../../components/hero/hero-no-card';
import withSplitTest from '../../components/with-split-test';

// Utils
import { extractLayoutProps } from '../../utils/layout';
import { sortDays, isValid } from '../../components/opening-times/helpers.js';
import { useSiteMetadata } from '../../hooks/site-metadata';

const getOpeningTimes = (shop) => {
  const times = shop.openingTimes
    ? JSON.parse(shop.openingTimes.internal.content)
    : null;

  return Object.keys(times)
    .sort(sortDays)
    .map((day) => times[day])
    .filter((day) => isValid && !day.closed)
    .map((day) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: `https://schema.org/${day.label}`,
      opens: day.opens,
      closes: day.closes,
    }));
};

export const Head = ({ data }) => {
  const { title, heroImage, navigation, shop } = data.page;
  const { longAddressLine1, longAddressLine2, longAddressPostcode } = shop;
  const { liveSiteURL } = useSiteMetadata();

  // Local business schema: https://schema.org/LocalBusiness
  const pageStructuredData = {
    // Required
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: title,
    address: {
      '@type': 'PostalAddress',
      streetAddress: `${longAddressLine1}${
        longAddressLine2 ? ` ${longAddressLine2} ` : ''
      }`,
      postalCode: longAddressPostcode,
      addressLocality: longAddressLine2,
      addressRegion: shop.region?.split(' ')[0],
      addressCountry: 'GB',
    },

    // Optional
    telephone: shop.contactNumber,
    url: `${liveSiteURL}${navigation.path}`,
    openingHoursSpecification: getOpeningTimes(shop),
    geo: {
      '@type': 'GeoCoordinates',
      latitude: shop.location.lat,
      longitude: shop.location.lon,
    },
  };

  // Optional
  if (heroImage?.file?.url) {
    pageStructuredData.image = [
      `https:${heroImage.file.url}?fit=fill&w=1600&h=1600`,
      `https:${heroImage.file.url}?fit=fill&w=1600&h=1200`,
      `https:${heroImage.file.url}?fit=fill&w=1600&h=900`,
    ];
  }

  return (
    <GatsbyHead
      pageContent={data.page}
      pageStructuredData={pageStructuredData}
    />
  );
};

const Page = ({ data }) => {
  const { page } = data;

  const {
    title,
    shop,
    form,
    bodyCopy,
    assemblies,
    heroImage,
    footerAssemblies,
  } = page;

  const [formNode, setFormNode] = useState(null);
  const formRefCallback = useCallback((node) => setFormNode(node), []);
  return (
    <Layout {...extractLayoutProps(page, data.contentfulConfig)}>
      <article>
        <PageTitle twoThirds>
          <h1>{title}</h1>
        </PageTitle>
        <CollectionCTA
          formNode={formNode}
          phoneNumber={shop?.furnitureCollectionPhoneNumber}
          responseTapClass={shop?.responseTapClass}
        />
        <Container>
          <HeroNoCard
            content={{ image: heroImage, removeMarginBottom: false }}
          />
          <ShopInfo {...shop} />
          {bodyCopy ? <RichText richText={bodyCopy} /> : null}
          <Assemblies assemblies={assemblies} />
          {form && (
            <div ref={formRefCallback}>
              <FormAssembly contentfulData={form} />
            </div>
          )}
        </Container>
        <Assemblies assemblies={footerAssemblies} />
      </article>
    </Layout>
  );
};

Page.propTypes = {
  data: PropTypes.shape({
    page: PropTypes.object,
    contentfulConfig: PropTypes.object,
  }),
  pageContext: PropTypes.object,
};

export default withSplitTest(Page);

export const shopPageQuery = graphql`
  query shopPageTemplateQuery($contentful_id: String!) {
    page: contentfulPageShop(contentful_id: { eq: $contentful_id }) {
      ...PageShopFragment
    }
    ...NavigationFragment
  }
`;
